import axios from 'axios';
import { fetchUser } from './auth';
import { formToObj } from '../common/lib/transformSearchRequest';

const constructBaseUrl = () => __CONFIG__.apiBaseUrl;
const constructApiKey = () => __CONFIG__.apiKey;

const baseUrl = constructBaseUrl();
const apiKey = constructApiKey();
const columnsMap = {
  given_name: 'firstName',
  family_name: 'lastName',
  email: 'email',
  address: 'address',
  phone_number: 'phone',
  created_at: 'created_at',
  sso_verified: 'sso_verified',
  deleted_at: 'deleted_at',
  permissions: 'cognito_user_enabled',
  role: 'role',
  deleted_by_last_name: 'lastName',
  ascend: 'asc',
  descend: 'desc',
};

const instantiateAxios = (baseURL) => {
  const headers = {
    'Content-Type': 'application/json',
  };
  return axios.create({
    baseURL,
    headers,
    timeout: 30000,
    crossDomain: true,
    withCredentials: false,
  });
};

const create = () => {
  const axiosInstance = instantiateAxios(baseUrl);

  const fetchSingleUser = ({ userId }) => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = `admin/users/${userId}`;
    return axiosInstance.get(path, {
      headers: {
        Authorization: accessToken,
      },
      userId,
    });
  };

  const resetPassword = ({ comment, userId }) => {
    const path = `admin/users/${userId}/reset-password`;
    const user = fetchUser();
    const { accessToken } = user;
    return axiosInstance.post(
      path,
      {
        comment,
      },
      {
        headers: {
          Authorization: accessToken,
        },
      },
    );
  };

  const updateUser = ({ comment, profileUpdate, userId }) => {
    const path = `admin/users/${userId}`;
    const user = fetchUser();
    const { accessToken } = user;
    return axiosInstance.patch(
      path,
      {
        comment,
        profileUpdate,
      },
      {
        headers: {
          Authorization: accessToken,
        },
      },
    );
  };

  const fetchUsers = ({
    searchParams, limit, offset, markedForDeletion, order, column,
  }) => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = 'admin/users';
    const params = formToObj(searchParams);
    params.limit = limit;
    params.offset = offset;
    params.column = columnsMap[column] || ''; // Columns on backend are firstName, lastName, address, email, phone
    params.order = columnsMap[order] || ''; // Order is default asc (if column is specified), desc is a specific trait
    params.marked_for_deletion = markedForDeletion;
    return axiosInstance.get(path, {
      headers: {
        Authorization: accessToken,
      },
      params,
    });
  };

  const fetchAdmins = ({
    searchParams, limit, offset, column, order,
  }) => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = 'admin/admin_users';
    const params = formToObj(searchParams);
    params.limit = limit;
    params.offset = offset;
    params.column = columnsMap[column] || '';
    params.order = columnsMap[order] || '';

    return axiosInstance.get(path, {
      headers: {
        Authorization: accessToken,
      },
      params,
    });
  };

  const fetchPurgedUser = ({ userDbId }) => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = `admin/purged-users/${userDbId}`;
    return axiosInstance.get(path, {
      headers: {
        Authorization: accessToken,
      },
    });
  };

  const fetchPurgedUsers = ({
    searchParams, limit, offset, column, order,
  }) => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = 'admin/purged-users';
    const params = formToObj(searchParams);
    params.limit = limit;
    params.offset = offset;
    params.column = columnsMap[column] || '';
    params.order = columnsMap[order] || '';
    return axiosInstance.get(path, {
      headers: {
        Authorization: accessToken,
      },
      params,
    });
  };

  const fetchUserAuditTrail = ({ limit, offset, userDbId }) => {
    const user = fetchUser();
    const { accessToken } = user;
    const params = {
      limit,
      offset,
    };
    const path = `admin/user-audits/${userDbId}`;
    return axiosInstance.get(path, {
      headers: {
        Authorization: accessToken,
      },
      params,
    });
  };

  const postUserAuditTrailComment = ({ userDbId, body }) => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = `admin/user-audits/${userDbId}`;
    return axiosInstance.post(path, body, {
      headers: {
        Authorization: accessToken,
      },
    });
  };

  const fetchFeaturesAuditTrail = ({ limit, offset }) => {
    const user = fetchUser();
    const { accessToken } = user;
    const params = {
      limit,
      offset,
    };
    const path = 'admin/features-audit-log';
    return axiosInstance.get(path, {
      headers: {
        Authorization: accessToken,
      },
      params,
    });
  };

  const postFeaturesAuditTrailComment = ({ body }) => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = 'admin/features-audit-log';
    return axiosInstance.post(path, body, {
      headers: {
        Authorization: accessToken,
      },
    });
  };

  const fetchFeaturesSettings = () => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = 'admin/features';
    return axiosInstance.get(path, {
      headers: {
        Authorization: accessToken,
      },
    });
  };

  const putTicketscanSettings = (body) => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = 'admin/features/ticket-scan';
    return axiosInstance.put(path, body, {
      headers: {
        Authorization: accessToken,
      },
    });
  };

  const getDailyLimit = (params = {}) => {
    const user = fetchUser();
    const { accessToken } = user;
    const gameType = params.gameType || 'spin_to_win';
    const path = 'admin/daily-limits';
    return axiosInstance.get(path, {
      headers: {
        Authorization: accessToken,
      },
      params: {
        gameType,
      },
    });
  };

  const setDailyLimit = (body, params = {}) => {
    const user = fetchUser();
    const { accessToken } = user;
    const gameType = params.gameType || 'spin_to_win';
    const path = 'admin/daily-limits';
    const updatedBody = {
      ...body,
      gameType,
    };
    return axiosInstance.put(path, updatedBody, {
      headers: {
        Authorization: accessToken,
      },
      params: {
        gameType,
      },
    });
  };

  // planning ahead so that we can pick user pool if migration from the
  // sso-app pool gets held up for some reason. just in case.
  const getUserBulkPath = (userPoolId, action) => `admin/pools/${userPoolId}/users/_bulk/${action}`;

  const handleUserBulkApiCall = (path, { users, comment }) => {
    const user = fetchUser();
    const { accessToken } = user;
    const body = {
      users,
      comment,
    };
    const config = {
      headers: {
        Authorization: accessToken,
      },
    };
    return axiosInstance
      .post(path, body, config)
      .then(({ data }) => data)
      .catch((error) => {
        if (error.response) {
          throw error;
        }
        // Connection error or something
        throw new Error('Something went wrong!');
      });
  };

  const bulkVerify = (userPoolId, { users, comment }) => {
    const path = getUserBulkPath(userPoolId, 'verify');
    return handleUserBulkApiCall(path, {
      users,
      comment,
    });
  };

  const bulkUnverify = (userPoolId, { users, comment }) => {
    const path = getUserBulkPath(userPoolId, 'unverify');
    return handleUserBulkApiCall(path, {
      users,
      comment,
    });
  };

  const bulkLockAccount = (userPoolId, { users, comment }) => {
    const path = getUserBulkPath(userPoolId, 'lock');
    return handleUserBulkApiCall(path, {
      users,
      comment,
    });
  };

  const bulkUnlockAccount = (userPoolId, { users, comment }) => {
    const path = getUserBulkPath(userPoolId, 'unlock');
    return handleUserBulkApiCall(path, {
      users,
      comment,
    });
  };

  const bulkMarkForDeletion = (userPoolId, { users, comment }) => {
    const path = getUserBulkPath(userPoolId, 'mark-for-deletion');
    return handleUserBulkApiCall(path, {
      users,
      comment,
    });
  };

  const bulkCancelDeletion = (userPoolId, { users, comment }) => {
    const path = getUserBulkPath(userPoolId, 'cancel-deletion');
    return handleUserBulkApiCall(path, {
      users,
      comment,
    });
  };

  const bulkPurge = (userPoolId, { users, comment }) => {
    const path = getUserBulkPath(userPoolId, 'purge');
    return handleUserBulkApiCall(path, {
      users,
      comment,
    });
  };

  const inviteAdmin = ({ body }) => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = 'admin/invite';
    return axiosInstance.post(path, body, {
      headers: {
        Authorization: accessToken,
      },
    });
  };

  const editAdmin = ({ userId, body }) => {
    const user = fetchUser();
    const { accessToken } = user;
    return axiosInstance.patch(`admin/admin_users/${userId}`, body, {
      headers: {
        Authorization: accessToken,
      },
    });
  };

  const toggleAdmin = ({ userId, body }) => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = `admin/admin_users/${userId}/status`;
    return axiosInstance.put(path, body, {
      headers: {
        Authorization: accessToken,
      },
    });
  };

  const logOutAdmin = () => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = 'admin/logout';
    const body = {
      accessToken,
    };
    return axiosInstance.post(path, body, {
      headers: {
        Authorization: accessToken,
      },
    });
  };

  const toggleGameData = (name, enabled) => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = `/game/toggle-game-data/${name}`;
    const body = {
      enabled,
    };
    return axiosInstance.put(path, body, {
      headers: {
        'x-api-key': apiKey,
        Authorization: accessToken,
      },
    });
  };

  const customGameMessage = (name, message) => {
    const user = fetchUser();
    const { accessToken } = user;
    const path = `/game/custom-message/${name}`;
    const body = {
      message,
    };
    return axiosInstance.put(path, body, {
      headers: {
        'x-api-key': apiKey,
        Authorization: accessToken,
      },
    });
  };

  const getGameToggleData = () => {
    const path = 'content/game-toggle?populate=deep,10';
    return axiosInstance.get(
      path,
      {
        headers: {
          'x-api-key': apiKey,
        },
      },
    );
  };

  const fetchEvents = (params) => {
    const path = 'events/admin/events';

    return axiosInstance.get(
      path,
      {
        headers: {
          'x-api-key': apiKey,
        },
        params,
      },
    );
  };

  const createEvent = ({
    name, points, restrictionsGames, restrictionsFirstTime, status,
  }) => {
    const body = {
      eventName: name,
      description: '',
      points,
      restrictionsGames,
      restrictionsFirstTime,
      status,
      eventType: 'default',
      metadataSchema: {},
    };

    const path = 'events/admin/events';

    const user = fetchUser();
    const { accessToken } = user;

    return axiosInstance.post(
      path,
      body,
      {
        headers: {
          'x-api-key': apiKey,
          Authorization: accessToken,
        },
      },
    );
  };

  const updateEvent = (event) => {
    const path = `events/admin/events/${event.event_id}`;

    const user = fetchUser();
    const { accessToken } = user;

    const body = {
      name: event.name,
      description: event.description,
      points: event.points,
      restrictionsGames: event.restrictionsGames,
      restrictionsFirstTime: event.restrictionsFirstTime,
      status: event.status,
      eventType: 'default',
      metadataSchema: event.metadata_schema,
    };

    return axiosInstance.put(
      path,
      body,
      {
        headers: {
          'x-api-key': apiKey,
          Authorization: accessToken,
        },
      },
    );
  };

  const markEventForDeletion = (eventName) => {
    const path = 'events/admin/mark-event-for-deletion';
    const body = { eventName };

    return axiosInstance.put(
      path,
      body,
      {
        headers: {
          'x-api-key': apiKey,
        },
      },
    );
  };

  const fetchGames = () => {
    const path = 'bid/igt/draw-games';

    return axiosInstance.get(
      path,
      {
        headers: {
          'x-api-key': apiKey,
        },
      },
    );
  };

  const fetchEventById = (eventId) => {
    const path = `events/admin/events/${eventId}`;
    return axiosInstance.get(
      path,
      {
        headers: {
          'x-api-key': apiKey,
        },
      },
    );
  };

  const fetchEventLog = (params) => {
    const path = 'events/admin/events/audit-log';

    return axiosInstance.get(
      path,
      {
        headers: {
          'x-api-key': apiKey,
        },
        params,
      },
    );
  };

  const fetchEventHistory = ({ userId, limit, offset }) => {
    const path = `events/history?ssoId=${userId}&limit=${limit}&offset=${offset}`;

    const user = fetchUser();
    const { accessToken } = user;

    return axiosInstance.get(
      path,
      {
        headers: {
          'x-api-key': apiKey,
          Authorization: accessToken,
        },
      },
    );
  };

  // eslint-disable-next-line arrow-body-style
  const fetchLoyaltyUser = (userId) => {
    const path = 'loyalty/admin/users';

    const user = fetchUser();
    const { accessToken } = user;

    return axiosInstance.get(
      path,
      {
        headers: {
          'x-api-key': apiKey,
          Authorization: accessToken,
        },
        params: {
          ssoId: userId,
        },
      },
    );
  };

  return {
    bulkVerify,
    bulkUnverify,
    bulkLockAccount,
    bulkUnlockAccount,
    bulkMarkForDeletion,
    bulkCancelDeletion,
    bulkPurge,
    fetchSingleUser,
    fetchUsers,
    fetchAdmins,
    fetchPurgedUser,
    fetchPurgedUsers,
    fetchUserAuditTrail,
    postUserAuditTrailComment,
    fetchFeaturesAuditTrail,
    postFeaturesAuditTrailComment,
    resetPassword,
    updateUser,
    fetchFeaturesSettings,
    putTicketscanSettings,
    getDailyLimit,
    setDailyLimit,
    inviteAdmin,
    editAdmin,
    toggleAdmin,
    logOutAdmin,
    toggleGameData,
    customGameMessage,
    getGameToggleData,
    fetchEvents,
    createEvent,
    updateEvent,
    markEventForDeletion,
    fetchGames,
    fetchEventById,
    fetchEventLog,
    fetchEventHistory,
    fetchLoyaltyUser,
  };
};

export default {
  create,
};
